import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios to include auth token
const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    localStorage.setItem("authToken", token);
  } else {
    delete axios.defaults.headers.common["Authorization"];
    localStorage.removeItem("authToken");
  }
};

const UserContext = createContext(null);

// Flag to prevent multiple logout calls
let isLoggingOut = false;

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const logoutRef = useRef(null);

  // Setup axios interceptor to handle 401 errors globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Only handle 401 for auth endpoints, not for resource endpoints
        // This prevents logout when accessing non-existent candidate profiles, etc.
        if (error.response?.status === 401) {
          const requestUrl = error.config?.url || "";
          // Only logout if the 401 is from an auth-related endpoint
          if (requestUrl.includes("/auth/me") || requestUrl.includes("/auth/login")) {
            if (!isLoggingOut && logoutRef.current) {
              isLoggingOut = true;
              console.log("Session expired, logging out...");
              logoutRef.current();
              setTimeout(() => { isLoggingOut = false; }, 1000);
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    // Check for stored token first (new JWT auth)
    const storedToken = localStorage.getItem("authToken");
    if (storedToken) {
      setAuthToken(storedToken);
      fetchUserFromToken(storedToken);
    } else {
      // Fallback: check for legacy email-only auth
      const storedEmail = localStorage.getItem("userEmail");
      if (storedEmail) {
        fetchUserLegacy(storedEmail);
      } else {
        setLoading(false);
      }
    }
  }, []);

  // Fetch user using JWT token
  const fetchUserFromToken = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      // Also store email for backward compatibility
      localStorage.setItem("userEmail", response.data.email);
    } catch (error) {
      console.error("Token validation failed:", error);
      
      // Only clear session if it's a 401 (unauthorized) or 403 (forbidden)
      // Don't logout on network errors or server errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Token invalid or expired - clear everything
        setAuthToken(null);
        localStorage.removeItem("userEmail");
        setUser(null);
      } else {
        // Network error or server error - try to use cached email
        const cachedEmail = localStorage.getItem("userEmail");
        if (cachedEmail) {
          // Keep user logged in with basic info from cache
          setUser({ email: cachedEmail, plan: "free", isOffline: true });
        } else {
          setUser(null);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Legacy: fetch user by email (for backward compatibility)
  const fetchUserLegacy = async (email) => {
    try {
      const response = await axios.get(`${API}/users/${encodeURIComponent(email)}`);
      setUser(response.data);
    } catch (error) {
      // User doesn't exist, will be created on first action
      setUser({ email, isNew: true, plan: "free" });
    } finally {
      setLoading(false);
    }
  };

  // New: Login with JWT token (used by new auth pages)
  const loginWithToken = (token, userData) => {
    setAuthToken(token);
    setUser(userData);
    localStorage.setItem("userEmail", userData.email);
  };

  // Legacy: Login with just email (backward compatibility)
  const loginWithEmail = async (email) => {
    try {
      const response = await axios.post(`${API}/users`, { email });
      setUser(response.data);
      localStorage.setItem("userEmail", email);
      return response.data;
    } catch (error) {
      console.error("Error creating user:", error);
      throw error;
    }
  };

  const logout = useCallback(() => {
    setUser(null);
    setAuthToken(null);
    localStorage.removeItem("userEmail");
  }, []);

  // Set logout ref for interceptor
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  const updateSettings = async (settings) => {
    if (!user?.email) return;
    try {
      await axios.put(`${API}/users/${encodeURIComponent(user.email)}/settings`, settings);
      setUser({ ...user, ...settings });
    } catch (error) {
      console.error("Error updating settings:", error);
      throw error;
    }
  };

  // Function to require email before performing an action
  const requireEmail = useCallback((action) => {
    if (user) {
      // User already logged in, execute action immediately
      action();
      return true;
    } else {
      // Show email capture and save the pending action
      setPendingAction(() => action);
      setShowEmailCapture(true);
      return false;
    }
  }, [user]);

  // Clear pending action when email capture closes
  const handleSetShowEmailCapture = useCallback((show) => {
    setShowEmailCapture(show);
    if (!show) {
      setPendingAction(null);
    }
  }, []);

  // Check if user is authenticated
  const isAuthenticated = !!user && !user.isNew;

  // Check if user has password set
  const hasPassword = user?.password_hash !== null && user?.password_hash !== undefined;

  return (
    <UserContext.Provider value={{ 
      user, 
      loading, 
      isAuthenticated,
      hasPassword,
      loginWithToken,
      loginWithEmail, 
      logout, 
      updateSettings,
      showEmailCapture,
      setShowEmailCapture: handleSetShowEmailCapture,
      pendingAction,
      requireEmail
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
